/**
 * SharedAnalysis — Public page for viewing a shared AI health analysis.
 * Accessed via /shared-analysis/:token (no auth required).
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Heart, Activity, Sparkles, AlertTriangle, CheckCircle, Loader2,
  Leaf, Stethoscope, FlaskConical, UserSearch, Droplets, Sun,
  Shield, Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SharedAnalysis = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchShared = async () => {
      try {
        const res = await fetch(`${API_URL}/api/ml-health/shared/${token}`);
        if (res.status === 410) {
          setError('expired');
        } else if (!res.ok) {
          setError('not_found');
        } else {
          setData(await res.json());
        }
      } catch {
        setError('network');
      }
      setLoading(false);
    };
    fetchShared();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading shared analysis...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center" data-testid="shared-error">
          <AlertTriangle className="w-14 h-14 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {error === 'expired' ? 'Link Expired' : 'Analysis Not Found'}
          </h1>
          <p className="text-gray-600">
            {error === 'expired'
              ? 'This share link has expired. Please ask the patient to generate a new one.'
              : 'The link is invalid or has been removed.'}
          </p>
        </div>
      </div>
    );
  }

  const a = data.analysis || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50" data-testid="shared-analysis-page">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-5 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-orange-500" />
              Shared AI Health Analysis
            </h1>
            <p className="text-sm text-gray-500 mt-1">Read-only view for medical review</p>
          </div>
          <div className="text-right text-sm text-gray-500">
            <p>Generated {new Date(data.created_at).toLocaleDateString()}</p>
            <p>Expires {new Date(data.expires_at).toLocaleDateString()}</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Patient Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6" data-testid="shared-patient-card">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-14 h-14 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full flex items-center justify-center text-white text-xl font-bold">
              {(data.patient_name || 'P')[0].toUpperCase()}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900" data-testid="shared-patient-name">{data.patient_name}</h2>
              <p className="text-sm text-gray-500" data-testid="shared-patient-id">Patient ID: {data.patient_id}</p>
            </div>
          </div>

          {/* Score + Vitals row */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-gradient-to-br from-orange-50 to-amber-100 rounded-xl p-4 text-center">
              <div className="w-14 h-14 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-xl font-bold text-white">{a.health_score ?? '—'}</span>
              </div>
              <p className="font-medium text-sm text-gray-900">Health Score</p>
              <p className="text-xs text-orange-600">{a.overall_status}</p>
            </div>
            <div className="bg-red-50 rounded-xl p-4 text-center">
              <Heart className="w-6 h-6 text-red-500 mx-auto mb-1" />
              <p className="text-xl font-bold text-gray-900">{a.vitals?.heart_rate || '—'}</p>
              <p className="text-xs text-gray-500">HR (bpm)</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <Activity className="w-6 h-6 text-blue-500 mx-auto mb-1" />
              <p className="text-xl font-bold text-gray-900">{a.vitals?.blood_pressure || '—'}</p>
              <p className="text-xs text-gray-500">BP (mmHg)</p>
            </div>
            <div className="bg-cyan-50 rounded-xl p-4 text-center">
              <Droplets className="w-6 h-6 text-cyan-500 mx-auto mb-1" />
              <p className="text-xl font-bold text-gray-900">{a.vitals?.spo2 || '—'}%</p>
              <p className="text-xs text-gray-500">SpO2</p>
            </div>
            <div className="bg-orange-50 rounded-xl p-4 text-center">
              <Sun className="w-6 h-6 text-orange-500 mx-auto mb-1" />
              <p className="text-xl font-bold text-gray-900">{a.vitals?.temperature || '—'}</p>
              <p className="text-xs text-gray-500">Temp (F)</p>
            </div>
          </div>
        </div>

        {/* Key Findings */}
        {a.key_findings?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="font-bold text-gray-900 mb-3">Key Findings</h3>
            <ul className="space-y-2">
              {a.key_findings.map((f, i) => (
                <li key={i} className="flex gap-2 text-gray-700 text-sm"><Shield className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />{f}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Two-column: Allopathic + Ayurvedic */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Allopathic */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Stethoscope className="w-5 h-5 text-blue-600" />
              <h3 className="font-bold text-gray-900">Allopathic Recommendations</h3>
            </div>
            <ul className="space-y-2 mb-4">
              {(a.allopathic_recommendations || []).map((r, i) => (
                <li key={i} className="flex gap-2 text-sm text-gray-700"><CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />{r}</li>
              ))}
            </ul>
            {a.corrective_measures?.length > 0 && (
              <>
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-1 text-sm"><AlertTriangle className="w-4 h-4 text-amber-500" /> Corrective Measures</h4>
                <ul className="space-y-2">
                  {a.corrective_measures.map((m, i) => (
                    <li key={i} className="flex gap-2 text-sm text-gray-700"><span className="text-amber-500 font-bold">{i + 1}.</span>{m}</li>
                  ))}
                </ul>
              </>
            )}
          </div>

          {/* Ayurvedic */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Leaf className="w-5 h-5 text-green-600" />
              <h3 className="font-bold text-gray-900">Ayurvedic Recommendations</h3>
            </div>
            {a.prakriti && (
              <div className="p-3 bg-green-50 rounded-lg mb-4 text-sm">
                <strong>Prakriti:</strong> {a.prakriti.dominant_dosha} — {a.prakriti.assessment}
              </div>
            )}
            <ul className="space-y-2">
              {(a.ayurvedic_recommendations || []).map((r, i) => (
                <li key={i} className="flex gap-2 text-sm text-gray-700"><Leaf className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />{r}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Risk Assessment */}
        {a.risk_assessment && Object.keys(a.risk_assessment).length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="font-bold text-gray-900 mb-4">Risk Assessment</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(a.risk_assessment).map(([key, val]) => (
                <div key={key} className="text-center p-4 rounded-xl bg-gray-50 border">
                  <p className="text-sm text-gray-500 capitalize mb-1">{key}</p>
                  <p className={`text-lg font-bold ${val.level === 'low' ? 'text-green-600' : val.level === 'moderate' ? 'text-amber-600' : 'text-red-600'}`}>
                    {val.level?.toUpperCase()}
                  </p>
                  <p className="text-xs text-gray-400">Score: {val.score}/100</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Suggested Tests */}
        {a.suggested_tests?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <FlaskConical className="w-5 h-5 text-indigo-600" />
              <h3 className="font-bold text-gray-900">Suggested Diagnostic Tests</h3>
            </div>
            <div className="space-y-3">
              {a.suggested_tests.map((t, i) => (
                <div key={i} className="p-3 bg-gray-50 rounded-lg border-l-4 text-sm text-gray-700" style={{ borderColor: i < 2 ? '#ef4444' : '#3b82f6' }}>
                  {typeof t === 'string' ? t : t.name}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lifestyle Tips */}
        {a.lifestyle_tips?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-orange-600" />
              <h3 className="font-bold text-gray-900">Lifestyle Recommendations</h3>
            </div>
            <ul className="space-y-2">
              {a.lifestyle_tips.map((tip, i) => (
                <li key={i} className="flex gap-2 text-sm text-gray-700"><CheckCircle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />{tip}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Doctor Referral */}
        {a.ayurvedic_doctor_referral?.recommended && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-2 mb-3">
              <UserSearch className="w-5 h-5 text-teal-600" />
              <h3 className="font-bold text-gray-900">Ayurvedic Doctor Referral</h3>
            </div>
            <p className="text-sm text-gray-700 mb-1"><strong>Reason:</strong> {a.ayurvedic_doctor_referral.reason}</p>
            <p className="text-sm text-gray-700"><strong>Specialization:</strong> {a.ayurvedic_doctor_referral.specialization}</p>
          </div>
        )}

        {/* Data Sources */}
        {a.data_sources && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="font-bold text-gray-900 mb-3">Data Sources Used</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center text-sm">
              <div className="p-3 bg-orange-50 rounded-lg"><p className="text-xl font-bold text-gray-900">{a.data_sources.wearable_readings}</p><p className="text-gray-500">Wearable Readings</p></div>
              <div className="p-3 bg-blue-50 rounded-lg"><p className="text-xl font-bold text-gray-900">{a.data_sources.lab_reports}</p><p className="text-gray-500">Lab Reports</p></div>
              <div className="p-3 bg-green-50 rounded-lg"><p className="text-xl font-bold text-gray-900">{a.data_sources.prescriptions}</p><p className="text-gray-500">Prescriptions</p></div>
              <div className="p-3 bg-purple-50 rounded-lg"><p className="text-xl font-bold text-gray-900">{a.data_sources.vital_records}</p><p className="text-gray-500">Vital Records</p></div>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 text-sm text-amber-800">
          <strong>Disclaimer:</strong> This AI-generated analysis is for informational purposes only and does not constitute medical advice. Please consult qualified healthcare professionals for diagnosis and treatment.
        </div>

        <div className="text-center text-xs text-gray-400 pb-6">
          Powered by HealthTrack Pro — Dual Medical Analysis Engine (Allopathic + Ayurvedic)
        </div>
      </main>
    </div>
  );
};

export default SharedAnalysis;
